import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ActivePlanCard from './components/ActivePlanCard/ActivePlanCard';
import ActivePlansCardSkeleton from './components/ActivePlansCardSkeleton/ActivePlansCardSkeleton';
import SectionCard from '../../../../../../shared/components/Cards/SectionCard/SectionCard';
import { useBroker } from '../../../../../../shared/hooks/useBroker/useBroker';
import { useUser } from '../../../../../../shared/hooks/useUser/useUser';
import type { IClientPlanApi } from '../../../../../../shared/interfaces/clients';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import ClientsService from '../../../../../../shared/services/api/clients/clients';
import { TRANSLATIONS_CLIENTS } from '../../../../../../shared/translations/clients';
import { ClientsStatus } from '../../../../../../shared/types/clients';
import { mapApiPlansToClientPlans, mapQuotesToClientPlans } from '../../../../../../shared/utils/clients/clientPlans';
import { isAppOnly } from '../../../../../../shared/utils/prospects/prospects';

import './ActivePlansCard.scss';

const PROFILE = TRANSLATIONS_CLIENTS.profile;
const PLANS = PROFILE.plans;

interface IActivePlansCardProps {
  clientId: number;
  status: ClientsStatus;
  quotes: IQuote[];
  quotesLoading: boolean;
  prospect: IProspect | null;
  onRefresh?: () => void;
}

const ActivePlansCard = ({ clientId, status, quotes, quotesLoading, prospect, onRefresh }: IActivePlansCardProps) => {
  const { t } = useTranslation();
  const { brokerId } = useUser();
  const { commercialAddons, commercialPlans } = useBroker();
  const isAwaiting = status === ClientsStatus.AwaitingStart;
  const appOnly = isAppOnly(prospect);
  const hasActivationError = isAwaiting && Boolean(prospect?.error);

  const [apiPlans, setApiPlans] = useState<IClientPlanApi[]>([]);
  const [apiPlansLoading, setApiPlansLoading] = useState(!isAwaiting);

  const enrollmentWindowDays =
    commercialAddons
      .flatMap((typeGroup) => typeGroup.groups)
      .map((entry) => entry.group.choiceWindowDays)
      .find((days): days is number => days != null) ?? 0;

  useEffect(() => {
    if (isAwaiting) return;

    if (!brokerId || Number.isNaN(clientId)) {
      setApiPlans([]);
      setApiPlansLoading(false);
      return;
    }

    let cancelled = false;
    setApiPlansLoading(true);

    const fetchPlans = async () => {
      try {
        const result = await ClientsService.getClientPlans(brokerId, clientId);
        if (!cancelled) setApiPlans(result);
      } catch {
        if (!cancelled) setApiPlans([]);
      } finally {
        if (!cancelled) setApiPlansLoading(false);
      }
    };

    void fetchPlans();

    return () => {
      cancelled = true;
    };
  }, [brokerId, clientId, isAwaiting]);

  const plans = useMemo(() => {
    if (isAwaiting) return mapQuotesToClientPlans(quotes, commercialPlans);

    const planQuoteIds = new Set(apiPlans.map((plan) => plan.quoteId));
    const quotesNotInPlans = quotes.filter((quote) => !planQuoteIds.has(quote.id));
    return [
      ...mapApiPlansToClientPlans(apiPlans, quotes, commercialPlans),
      ...mapQuotesToClientPlans(quotesNotInPlans, commercialPlans)
    ];
  }, [isAwaiting, quotes, apiPlans, commercialPlans]);

  const loading = isAwaiting ? quotesLoading : apiPlansLoading || quotesLoading;

  const hasPlans = plans.length > 0;

  const renderBody = () => {
    if (loading) return <ActivePlansCardSkeleton />;

    if (!hasPlans) {
      return (
        <div className="active-plans-card__empty">
          <p className="active-plans-card__empty-title">{t(PLANS.empty.title)}</p>
          <p className="active-plans-card__empty-desc">{t(PLANS.empty.description)}</p>
        </div>
      );
    }

    return (
      <div className="active-plans-card__list">
        {plans.map((plan) => (
          <ActivePlanCard
            key={plan.id}
            plan={plan}
            enrollmentWindowDays={enrollmentWindowDays}
            appOnly={appOnly}
            hasActivationError={hasActivationError}
            quote={quotes.find((quote) => quote.id === plan.quoteId)}
            brokerId={brokerId ?? undefined}
            onQuoteUpdate={() => onRefresh?.()}
          />
        ))}
      </div>
    );
  };

  return (
    <SectionCard className="active-plans-card" title={t(PROFILE.sections.activePlans)}>
      {renderBody()}
    </SectionCard>
  );
};

export default ActivePlansCard;
